import React, {
  FC,
  ReactElement,
  useState,
  useRef,
  LegacyRef,
  KeyboardEvent,
  useEffect,
  useCallback,
} from 'react';
import { useFormik } from 'formik';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Checkbox, useMediaQuery } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import NameApplicantField from '@components/Fields/NameApplicantField';
import EmailField from '@components/Fields/EmailField';
import DescriptionField from '@components/Fields/DescriptionField';
import { useFeedbackStore, useOwnProfileStore, useSignStore } from './hooks';
import { FormSchema } from './FormSchema';
import Captcha from '../Captcha/Captcha';
import Documents from '@public/pages/Documents';
import project, { isSpecialBilling } from '@core/constants/project';
import { documents } from '@public/Root';
import cn from 'classnames';
import { MINIO_END_POINT } from '@core/constants/minio';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  fromPublic?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actions: {
      display: 'flex',
      justifyContent: 'center',
    },
    button: {
      width: theme.spacing(32),
    },
    emailField: {
      marginBottom: '15px',
    },
    checkboxWrapper: {
      paddingRight: 9,

      [theme.breakpoints.down('xs')]: {
        flexWrap: 'nowrap',
        // justifyContent: 'center',
      },

      '@media (max-width: 372px)': {
        marginTop: 12,
      },
    },

    dontShow: {
      width: 'calc(100% - 42px)',
      fontSize: 15,
      [theme.breakpoints.down('xs')]: {
        width: 'auto',
      },
    },
    dontShowError: {
      color: 'red',
    },
    userLicenseLink: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
    },
  }),
);

const FORM_ID = 'feedback_dialog__form';

const FeedbackDialog: FC<FeedbackDialogProps> = ({ open, onClose, fromPublic }) => {
  const classes = useStyles();
  const { create } = useFeedbackStore();
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const { captchaVerify, idCaptcha, captchaGet } = useSignStore();
  const [captchaValue, setCaptchaValue] = useState<string>('');
  const [idCaptchaValue, setIdCaptchaValue] = useState<string>('');
  const [captchaError, setCaptchaError] = useState<boolean>(false);
  const [
    openPersonalDataRegistrationAgreement,
    setOpenPersonalDataRegistrationAgreement,
  ] = useState(false);
  const [openPrivatPolicy, setOpenPrivatePolicy] = useState(false);

  const [
    checkboxPersonalDataRegistrationAgreement,
    setCheckboxPersonalDataRegistrationAgreement,
  ] = useState(false);

  const handleChangeCheckboxPersonalDataRegistrationAgreement = useCallback(() => {
    setCheckboxPersonalDataRegistrationAgreement(prev => !prev);
  }, []);

  useEffect(() => {
    if (idCaptcha) setIdCaptchaValue(idCaptcha);
  }, [idCaptcha]);

  useEffect(() => {
    if (checkboxPersonalDataRegistrationAgreement && !openPersonalDataRegistrationAgreement) {
      setOpenPersonalDataRegistrationAgreement(true);
    }
  }, [checkboxPersonalDataRegistrationAgreement]);

  useEffect(() => {
    if (checkboxPersonalDataRegistrationAgreement) {
      setFieldValue('personalDataRegistrationAgreement', true);
    }
  }, [checkboxPersonalDataRegistrationAgreement]);

  useEffect(() => {
    if (idCaptcha) setIdCaptchaValue(idCaptcha);
  }, [idCaptcha]);

  const { createConsent } = useOwnProfileStore();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: {
      nameApplicant: '',
      email: '',
      address: '',
      title: '',
      text: '',
      personalAccount: '',
      personalDataRegistrationAgreement: fromPublic
        ? checkboxPersonalDataRegistrationAgreement
        : true,
    },
    validationSchema: FormSchema,
    onSubmit: async values => {
      if (!captchaValue) {
        setCaptchaError(true);
        return {};
      }

      if (captchaValue) {
        const isHuman = await captchaVerify(captchaValue, idCaptchaValue);

        if (!isHuman) {
          setCaptchaError(true);
          captchaGet();
          setCaptchaValue('');

          return {};
        }
      }

      if (fromPublic) {
        const data: {
          consentType: string;
          email?: string;
          fullName?: string;
          personalAccount?: string;
          address?: string;
          phone?: string;
        }[] = [
          {
            consentType: 'personal_data_registration_agreement',
            email: values.email,
            fullName: values.nameApplicant,
            personalAccount: values.personalAccount,
            address: values.address,
          },
        ];
        createConsent(data).then(() => {
          create(
            {
              data: {
                title: values.title,
                body: `<b>Описание</b> <br>: ${values.text}
              ${values.address ? `<br>Адрес: ${values.address}` : ''}
              <br>Лицевой счет: ${values.personalAccount}`,
                fio: values.nameApplicant,
                email: values.email,
              },
            },
            { notification: true },
          );
        });
      } else {
        create(
          {
            data: {
              title: values.title,
              body: `<b>Описание</b> <br>: ${values.text}
              ${values.address ? `<br>Адрес: ${values.address}` : ''}
              <br>Лицевой счет: ${values.personalAccount}`,
              fio: values.nameApplicant,
              email: values.email,
            },
          },
          { notification: true },
        );
      }

      resetForm();

      onClose();
    },
  });

  const handleClose = (): void => {
    onClose();

    resetForm();
  };

  const titleElement: ReactElement = <DialogTitle>Обратная связь</DialogTitle>;

  const content: ReactElement = (
    <DialogContent>
      <form id={FORM_ID} noValidate={true} onSubmit={handleSubmit}>
        <Grid container={true} direction="column">
          <Grid item={true} container={true}>
            <NameApplicantField
              id={`${FORM_ID}__name-applicant`}
              label="Ваше имя"
              required={true}
              value={values.nameApplicant}
              placeholder=" "
              helperText=" "
              error={errors.nameApplicant}
              touched={touched.nameApplicant}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item={true} container={true}>
            <EmailField
              id={`${FORM_ID}__email`}
              className={classes.emailField}
              required={true}
              helperText="На этот адрес будет отправлен ответ"
              value={values.email}
              error={errors.email}
              touched={touched.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item={true} container={true}>
            <TextField
              id={`${FORM_ID}__title`}
              fullWidth={true}
              label="Заголовок"
              required={true}
              name="title"
              value={values.title}
              error={Boolean(errors.title && touched.title)}
              helperText={Boolean(errors.title && touched.title) ? errors.title : ' '}
              onChange={handleChange}
              onBlur={handleBlur}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item={true} container={true}>
            <DescriptionField
              id={`${FORM_ID}__description`}
              label="Причина"
              helperText=" "
              required={true}
              value={values.text}
              error={errors.text}
              touched={touched.text}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Grid>
          <Grid item={true} container={true}>
            <TextField
              id={`${FORM_ID}__address`}
              fullWidth={true}
              label="Адрес"
              name="address"
              value={values.address}
              error={Boolean(errors.address && touched.address)}
              helperText={Boolean(errors.address && touched.address) ? errors.address : ' '}
              onChange={handleChange}
              onBlur={handleBlur}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item={true} container={true}>
            <TextField
              id={`${FORM_ID}__personalAccount`}
              fullWidth={true}
              label="Лицевой счет"
              name="personalAccount"
              value={values.personalAccount}
              error={Boolean(errors.personalAccount && touched.personalAccount)}
              helperText={
                Boolean(errors.personalAccount && touched.personalAccount)
                  ? errors.personalAccount
                  : ' '
              }
              onChange={handleChange}
              onBlur={handleBlur}
              InputLabelProps={{
                shrink: true,
              }}
              required={true}
              inputProps={{ maxLength: 20, type: 'text' }}
            />
          </Grid>
          <Captcha
            error={captchaError}
            setErrorCaptcha={setCaptchaError}
            captchaValue={captchaValue}
            setCaptchaValue={setCaptchaValue}
          />
          {fromPublic && (
            <Grid container alignItems="center" className={classes.checkboxWrapper}>
              <Checkbox
                name="personalDataRegistrationAgreement"
                onChange={checkboxPersonalDataRegistrationAgreement => {
                  handleChange(checkboxPersonalDataRegistrationAgreement);
                  handleChangeCheckboxPersonalDataRegistrationAgreement();
                }}
                checked={checkboxPersonalDataRegistrationAgreement}
                // onClick={handleChangeCheckboxPersonalDataRegistrationAgreement}
                style={{ marginTop: '-34px' }}
                className={cn(errors.personalDataRegistrationAgreement && classes.dontShowError)}
              />
              <span
                className={cn(
                  classes.dontShow,
                  errors.personalDataRegistrationAgreement && classes.dontShowError,
                )}
              >
                Даю{' '}
                <span
                  className={classes.userLicenseLink}
                  onClick={() => setOpenPersonalDataRegistrationAgreement(true)}
                >
                  Согласие на обработку персональных данных
                </span>{' '}
                в соответствии с{' '}
                <span
                  className={classes.userLicenseLink}
                  onClick={() => setOpenPrivatePolicy(true)}
                >
                  Политикой обработки и обеспечения безопасности персональных данных
                </span>{' '}
              </span>
            </Grid>
          )}
        </Grid>
      </form>
    </DialogContent>
  );

  const actions: ReactElement = (
    <DialogActions>
      <Button
        className={classes.button}
        id={`${FORM_ID}__cancel`}
        color="primary"
        variant="outlined"
        size="small"
        onClick={handleClose}
      >
        Отмена
      </Button>
      <Button
        className={classes.button}
        id={`${FORM_ID}__close`}
        type="submit"
        form={FORM_ID}
        color="primary"
        variant="contained"
        size="small"
      >
        Отправить
      </Button>
    </DialogActions>
  );

  return (
    <>
      <Dialog open={open} fullWidth={true} maxWidth="sm" fullScreen={matches} onClose={handleClose}>
        {titleElement}
        {content}
        {actions}
      </Dialog>
      <Documents
        open={openPersonalDataRegistrationAgreement}
        setOpen={setOpenPersonalDataRegistrationAgreement}
        title={documents[2].title}
        uri={`${MINIO_END_POINT}/${
          isSpecialBilling ? 'billing' : project
        }/notsecured/00000000-0000-0000-0000-000000000000/${documents[2].uri}`}
        addingActionOnButton={
          !checkboxPersonalDataRegistrationAgreement
            ? handleChangeCheckboxPersonalDataRegistrationAgreement
            : undefined
        }
        buttonTitle="Принять"
      />
      <Documents
        open={openPrivatPolicy}
        setOpen={setOpenPrivatePolicy}
        title={documents[1].title}
        uri={`${MINIO_END_POINT}/${
          isSpecialBilling ? 'billing' : project
        }/notsecured/00000000-0000-0000-0000-000000000000/${documents[1].uri}`}
      />
    </>
  );
};

export default FeedbackDialog;
